import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn, isPWA } from "@/lib/utils";
import { useNotifications } from "@novu/react";
import { Link, useMatchRoute, useNavigate } from "@tanstack/react-router";
import {
  Bell,
  Crown,
  Flag,
  Handshake,
  Home,
  MonitorSmartphone,
  Plus,
  Search,
  Settings,
  ShoppingBag,
  Target,
  Trees,
  Trophy,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useCurrentUser } from "../data/auth/hooks";
import { useUserClubs } from "../data/clubs/hooks";
import { useMilestonesRecap } from "../data/milestones/hooks";
import { useAuthStore } from "../data/useAuthStore";
import { getFallbackName } from "../lib/name-utils";
import { NovuInbox } from "./inbox";
import { UploadActivityDialog } from "./upload-activity-dialog";

export function AppSidebar() {
  const { isMobile, state, toggleSidebar } = useSidebar();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();

  const { notifications } = useNotifications();

  const setActiveClubId = useAuthStore((state) => state.setActiveClubId);

  const { data: user } = useCurrentUser();
  const { data: userClubs } = useUserClubs();
  const { data: milestonesRecap } = useMilestonesRecap();

  const [uploadActivityOpen, setUploadActivityOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const showMilestonePulse = milestonesRecap?.hasClaimableMilestone ?? false;

  if (!user) {
    return null;
  }

  return (
    <>
      <NovuInbox open={notificationsOpen} onOpenChange={setNotificationsOpen} />

      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" tooltip="User Profile">
                <Avatar className="rounded-sm border-0">
                  <AvatarImage
                    src={user.avatarUrl}
                    alt={user.firstName || "User"}
                  />
                  <AvatarFallback className="text-sm font-medium">
                    {getFallbackName(user.firstName || "", user.lastName || "")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold truncate max-w-[150px]">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <div className="px-2 py-2">
            <h2
              className={cn(
                "mb-2 px-4 text-xs font-semibold tracking-tight text-muted-foreground",
                "group-data-[collapsible=icon]:hidden"
              )}
            >
              PLATFORM
            </h2>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Home"
                  isActive={!!matchRoute({ to: "/home" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/home"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Home className="mr-2 h-4 w-4" />
                      <span>Home</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Profiles"
                  isActive={!!matchRoute({ to: "/profiles" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/profiles"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      <span>Profiles</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Milestones"
                  isActive={!!matchRoute({ to: "/milestones" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/milestones"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Flag className="mr-2 h-4 w-4" />
                      <span
                        className={
                          !matchRoute({ to: "/milestones" }) &&
                          showMilestonePulse
                            ? "animate-pulse text-yellow-500"
                            : "data-[active=true]:text-blue-500"
                        }
                      >
                        Milestones
                      </span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Buddies"
                  isActive={!!matchRoute({ to: "/buddies" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/buddies"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Handshake className="mr-2 h-4 w-4" />
                      <span>Buddies</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Achievements"
                  isActive={!!matchRoute({ to: "/achievements" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/achievements"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Trophy className="mr-2 h-4 w-4" />
                      <span>Achievements</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Trees"
                  isActive={!!matchRoute({ to: "/trees" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-green-500 hover:text-green-300 transition-colors data-[active=true]:text-green-500"
                    asChild
                  >
                    <Link
                      to="/trees"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Trees className="mr-2 h-4 w-4" />
                      <span>Plant Trees</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem className="hidden">
                <SidebarMenuButton
                  asChild
                  tooltip="Clubs"
                  isActive={!!matchRoute({ to: "/clubs" })}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                    asChild
                  >
                    <Link
                      to="/clubs"
                      onClick={() =>
                        isMobile && state === "expanded" && toggleSidebar()
                      }
                      className="flex items-center"
                    >
                      <Crown className="mr-2 h-4 w-4" />
                      <span>Clubs</span>
                    </Link>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>

          {userClubs &&
            userClubs.map((club) => (
              <div className="px-2 py-2" key={club.id}>
                <h2
                  className={cn(
                    "mb-2 px-4 text-xs font-semibold tracking-tight text-muted-foreground",
                    "group-data-[collapsible=icon]:hidden",
                    "uppercase"
                  )}
                >
                  {club.name}
                </h2>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      tooltip="Teams"
                      isActive={!!matchRoute({ to: "/teams" })}
                    >
                      <Button
                        variant="ghost"
                        className="w-full justify-start transition-colors hover:text-blue-500"
                        asChild
                      >
                        <Link
                          to="/teams"
                          onClick={() => {
                            if (isMobile && state === "expanded")
                              toggleSidebar();
                            setActiveClubId(club.id);
                          }}
                          className="flex items-center"
                        >
                          <Users className="mr-2 h-4 w-4" />
                          <span>Teams</span>
                        </Link>
                      </Button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      tooltip="Missions"
                      isActive={!!matchRoute({ to: "/missions" })}
                    >
                      <Button
                        variant="ghost"
                        className="w-full justify-start transition-colors hover:text-blue-500"
                        asChild
                      >
                        <Link
                          to="/missions"
                          className="flex items-center"
                          onClick={() => {
                            if (isMobile && state === "expanded")
                              toggleSidebar();
                            setActiveClubId(club.id);
                          }}
                        >
                          <Target className="mr-2 h-4 w-4" />
                          <span>Missions</span>
                        </Link>
                      </Button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      tooltip="Shop"
                      isActive={!!matchRoute({ to: "/shop" })}
                    >
                      <Button
                        variant="ghost"
                        className="w-full justify-start transition-colors hover:text-blue-500"
                        asChild
                      >
                        <Link
                          to="/shop"
                          onClick={() => {
                            if (isMobile && state === "expanded")
                              toggleSidebar();
                            setActiveClubId(club.id);
                          }}
                          className="flex items-center"
                        >
                          <ShoppingBag className="mr-2 h-4 w-4" />
                          <span>Shop</span>
                        </Link>
                      </Button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      tooltip="Insights"
                      isActive={!!matchRoute({ to: "/insights" })}
                    >
                      <Button
                        variant="ghost"
                        className="w-full justify-start hover:text-blue-500 transition-colors"
                        asChild
                      >
                        <Link
                          to="/insights"
                          className="flex items-center"
                          onClick={() => {
                            if (isMobile && state === "expanded")
                              toggleSidebar();
                          }}
                        >
                          <Search className="mr-2 h-4 w-4" />
                          <span>Insights</span>
                        </Link>
                      </Button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {club.isAdmin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        tooltip="Settings"
                        isActive={!!matchRoute({ to: "/club-management" })}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start hover:text-blue-500 transition-colors"
                          asChild
                        >
                          <Link
                            to="/club-management"
                            className="flex items-center"
                            onClick={() => {
                              setActiveClubId(club.id);

                              if (isMobile && state === "expanded")
                                toggleSidebar();
                            }}
                          >
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Manage</span>
                          </Link>
                        </Button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </div>
            ))}
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Notifications">
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start transition-colors hover:text-blue-500"
                  )}
                  onClick={() => {
                    if (!isMobile) {
                      setNotificationsOpen(true);
                    } else {
                      navigate({ to: "/notifications" });
                    }

                    if (isMobile && state === "expanded") {
                      toggleSidebar();
                    }
                  }}
                >
                  <div className="mr-2 relative flex items-center justify-center h-4 w-4">
                    {notifications &&
                      notifications.length > 0 &&
                      notifications.some((n) => !n.isRead) && (
                        <span className="absolute top-0 right-0 inline-flex h-2 w-2 rounded-full bg-blue-600 animate-pulse"></span>
                      )}

                    <Bell className="h-4 w-4" />
                  </div>
                  <span>Notifications</span>
                </Button>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {isMobile && !isPWA() && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Install App">
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start transition-colors hover:text-blue-500"
                    )}
                    onClick={() => {
                      if (
                        // @ts-ignore
                        window.AddToHomeScreenInstance &&
                        // @ts-ignore
                        typeof window.AddToHomeScreenInstance.show ===
                          "function"
                      ) {
                        // @ts-ignore
                        window.AddToHomeScreenInstance.show("en");
                      }
                    }}
                  >
                    <MonitorSmartphone className="mr-2 h-4 w-4" />
                    <span>Install App</span>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Upload Activity">
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start transition-colors hover:text-blue-500"
                  )}
                  onClick={() => {
                    setUploadActivityOpen(true);

                    if (isMobile && state === "expanded") {
                      toggleSidebar();
                    }
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Upload Activity</span>
                </Button>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Settings"
                isActive={!!matchRoute({ to: "/settings" })}
              >
                <Button
                  variant="ghost"
                  className="w-full justify-start hover:text-blue-500 transition-colors data-[active=true]:text-blue-500"
                  asChild
                  onClick={() => {
                    if (isMobile && state === "expanded") toggleSidebar();
                  }}
                >
                  <Link to="/settings" className="flex items-center">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </Button>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <UploadActivityDialog
        open={uploadActivityOpen}
        onOpenChange={() => {
          setUploadActivityOpen(!uploadActivityOpen);
        }}
      />
    </>
  );
}
